import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { addDays, format, subDays } from "date-fns"
import { AlertTriangle, RefreshCw, Users, Utensils } from "lucide-react"
import { useCallback, useState } from "react"
import { z } from "zod"

import { CalendarService } from "@/client"
import { CalendarDateNavigation } from "@/components/Calendar/CalendarDateNavigation.tsx"
import { CalendarFreshnessIndicator } from "@/components/Calendar/CalendarFreshnessIndicator.tsx"
import { KitchenDietaryDetailModal } from "@/components/Calendar/KitchenDietaryDetailModal"
import {
  type KitchenGuest,
  type KitchenMealBreakdown,
  KitchenMealSection,
  MealSectionSkeleton,
} from "@/components/Calendar/KitchenMealSection"
import { KitchenWeekView } from "@/components/Calendar/KitchenWeekView"
import { OfflineBanner } from "@/components/Calendar/OfflineBanner.tsx"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { useOnlineStatus } from "@/hooks/useOnlineStatus"
import { usePullToRefresh } from "@/hooks/usePullToRefresh"
import { useSwipeNavigation } from "@/hooks/useSwipeNavigation"
import { parseLocalDate } from "@/lib/utils"

// Search params schema for URL state
const searchParamsSchema = z.object({
  date: z.string().optional(),
  view: z.enum(["day", "week"]).optional(),
})

export const Route = createFileRoute("/_layout/calendar/kitchen")({
  component: KitchenCalendarPage,
  validateSearch: searchParamsSchema,
  staticData: {
    pageTitle: "Kitchen Calendar",
    pageIcon: Utensils,
    pageIconStyle: { color: "#22A559" },
  },
  head: () => ({
    meta: [
      {
        title: "Kitchen Calendar - Broken Islands Booking",
      },
    ],
  }),
})

/**
 * Empty state for the calendar when no meals exist.
 */
interface EmptyCalendarStateProps {
  selectedDate: Date
  onGoToToday: () => void
}

function EmptyCalendarState({
  selectedDate,
  onGoToToday,
}: EmptyCalendarStateProps) {
  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        <Utensils className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">No meals for this day</h3>
        <p className="text-muted-foreground mb-4">
          There are no guests with meal requirements for{" "}
          {format(selectedDate, "MMMM d, yyyy")}
        </p>
        <Button variant="outline" onClick={onGoToToday}>
          <Utensils className="mr-2 h-4 w-4" />
          Go to Today
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * Error state for the calendar.
 */
interface CalendarErrorStateProps {
  onRetry: () => void
}

function CalendarErrorState({ onRetry }: CalendarErrorStateProps) {
  return (
    <Card className="border-destructive">
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        <RefreshCw className="h-12 w-12 text-destructive mb-4" />
        <h3 className="text-lg font-medium mb-2">Unable to load calendar</h3>
        <p className="text-muted-foreground mb-4">
          There was a problem loading the kitchen calendar.
        </p>
        <Button variant="outline" onClick={onRetry}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Try Again
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * Daily summary header component.
 */
interface KitchenDailySummaryProps {
  summary: {
    total_guests: number
    dietary_restrictions: number
    severe_allergies: number
  }
}

function KitchenDailySummary({ summary }: KitchenDailySummaryProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
      <Card>
        <CardContent className="p-3 sm:p-4 flex items-center gap-3">
          <div className="bg-green-500/10 p-2 rounded-lg">
            <Users className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
          </div>
          <div>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Total Guests
            </p>
            <p className="text-xl sm:text-2xl font-semibold">
              {summary.total_guests}
            </p>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-3 sm:p-4 flex items-center gap-3">
          <div className="bg-amber-500/10 p-2 rounded-lg">
            <Utensils className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
          </div>
          <div>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Dietary Restrictions
            </p>
            <p className="text-xl sm:text-2xl font-semibold">
              {summary.dietary_restrictions}
            </p>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-3 sm:p-4 flex items-center gap-3">
          <div
            className={`p-2 rounded-lg ${
              summary.severe_allergies > 0 ? "bg-red-500/10" : "bg-muted"
            }`}
          >
            <AlertTriangle
              className={`h-4 w-4 sm:h-5 sm:w-5 ${
                summary.severe_allergies > 0
                  ? "text-red-600"
                  : "text-muted-foreground"
              }`}
            />
          </div>
          <div>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Severe Allergies
            </p>
            <p
              className={`text-xl sm:text-2xl font-semibold ${
                summary.severe_allergies > 0 ? "text-red-600" : ""
              }`}
            >
              {summary.severe_allergies}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

/**
 * Main Kitchen Calendar page component.
 */
function KitchenCalendarPage() {
  const navigate = useNavigate({ from: Route.fullPath })
  const searchParams = Route.useSearch()
  const [selectedGuest, setSelectedGuest] = useState<KitchenGuest | null>(null)
  const { isOnline } = useOnlineStatus()

  // Parse selected date from URL or use today
  const selectedDate = searchParams.date
    ? parseLocalDate(searchParams.date)
    : new Date()

  // View mode from URL
  const viewMode = searchParams.view ?? "day"

  // Fetch calendar data (day view)
  const {
    data: calendarData,
    isLoading,
    isError,
    refetch,
    dataUpdatedAt,
    isRefetching,
  } = useQuery({
    queryKey: ["calendar", "kitchen", format(selectedDate, "yyyy-MM-dd")],
    queryFn: () =>
      CalendarService.getKitchenCalendar({
        requestedDate: format(selectedDate, "yyyy-MM-dd"),
      }),
    enabled: viewMode === "day",
    refetchInterval: viewMode === "day" ? 2 * 60 * 1000 : undefined,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
  })

  // Navigation handlers
  const handlePrevDay = useCallback(() => {
    const prevDate = subDays(selectedDate, 1)
    navigate({
      to: ".",
      search: { date: format(prevDate, "yyyy-MM-dd"), view: viewMode },
    })
  }, [selectedDate, navigate, viewMode])

  const handleNextDay = useCallback(() => {
    const nextDate = addDays(selectedDate, 1)
    navigate({
      to: ".",
      search: { date: format(nextDate, "yyyy-MM-dd"), view: viewMode },
    })
  }, [selectedDate, navigate, viewMode])

  const handleGoToToday = useCallback(() => {
    navigate({
      to: ".",
      search: { view: viewMode },
    })
  }, [navigate, viewMode])

  const handleDateSelect = useCallback(
    (date: Date) => {
      navigate({
        to: ".",
        search: { date: format(date, "yyyy-MM-dd"), view: viewMode },
      })
    },
    [navigate, viewMode],
  )

  const handleViewChange = useCallback(
    (newView: "day" | "week") => {
      navigate({
        to: ".",
        search: {
          date: searchParams.date,
          view: newView,
        },
      })
    },
    [navigate, searchParams.date],
  )

  const handleWeekDayClick = useCallback(
    (date: Date) => {
      navigate({
        to: ".",
        search: { date: format(date, "yyyy-MM-dd"), view: "day" },
      })
    },
    [navigate],
  )

  // Swipe navigation
  const swipeHandlers = useSwipeNavigation({
    onSwipeLeft: handleNextDay,
    onSwipeRight: handlePrevDay,
    minDistance: 50,
  })

  // Pull to refresh
  const pullHandlers = usePullToRefresh({
    onRefresh: async () => {
      await refetch()
    },
    threshold: 60,
    enabled: isOnline && viewMode === "day",
  })

  // Combined touch handlers
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      pullHandlers.onTouchStart(e)
      swipeHandlers.onTouchStart(e)
    },
    [pullHandlers, swipeHandlers],
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      pullHandlers.onTouchMove(e)
      swipeHandlers.onTouchMove(e)
    },
    [pullHandlers, swipeHandlers],
  )

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      pullHandlers.onTouchEnd(e)
      swipeHandlers.onTouchEnd()
    },
    [pullHandlers, swipeHandlers],
  )

  // Get calendar data safely
  const calendar = calendarData?.data as Record<string, unknown> | undefined
  const meals = calendar?.meals as
    | Record<string, KitchenMealBreakdown>
    | undefined
  const summary = calendar?.summary as
    | {
        total_guests: number
        dietary_restrictions: number
        severe_allergies: number
      }
    | undefined

  // Check if calendar has any guests
  const hasGuests =
    meals &&
    (meals.breakfast?.total_guests > 0 ||
      meals.lunch?.total_guests > 0 ||
      meals.dinner?.total_guests > 0)

  return (
    <div
      ref={pullHandlers.containerRef}
      className="container py-6 touch-pan-y overscroll-y-contain"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Offline Banner */}
      {!isOnline && <OfflineBanner className="mb-4" />}

      {/* Pull to refresh indicator */}
      {pullHandlers.isPulling && (
        <div
          className="flex justify-center py-2 transition-transform"
          style={{
            transform: `translateY(${Math.min(pullHandlers.pullDistance, 60)}px)`,
          }}
        >
          <RefreshCw
            className={`h-5 w-5 text-muted-foreground ${
              pullHandlers.pullDistance >= 60 ? "animate-spin" : ""
            }`}
          />
        </div>
      )}

      {/* Detail Modal */}
      <KitchenDietaryDetailModal
        guest={selectedGuest}
        open={!!selectedGuest}
        onOpenChange={(open) => {
          if (!open) setSelectedGuest(null)
        }}
      />

      {/* Date navigation bar */}
      <CalendarDateNavigation
        selectedDate={selectedDate}
        onPrevDay={handlePrevDay}
        onNextDay={handleNextDay}
        onGoToToday={handleGoToToday}
        onDateSelect={handleDateSelect}
        onViewModeChange={handleViewChange}
        viewMode={viewMode}
        className="mb-6"
      />

      {/* Content */}
      {viewMode === "week" ? (
        <KitchenWeekView
          selectedDate={selectedDate}
          onDayClick={handleWeekDayClick}
        />
      ) : isLoading ? (
        <div className="space-y-6">
          <KitchenDailySummary
            summary={{
              total_guests: 0,
              dietary_restrictions: 0,
              severe_allergies: 0,
            }}
          />
          <div className="space-y-4">
            <MealSectionSkeleton />
            <MealSectionSkeleton />
            <MealSectionSkeleton />
          </div>
        </div>
      ) : isError ? (
        <CalendarErrorState onRetry={() => refetch()} />
      ) : !hasGuests ? (
        <EmptyCalendarState
          selectedDate={selectedDate}
          onGoToToday={handleGoToToday}
        />
      ) : (
        <>
          {/* Daily summary */}
          {summary && <KitchenDailySummary summary={summary} />}

          {/* Freshness indicator */}
          {dataUpdatedAt && (
            <div className="mb-4">
              <CalendarFreshnessIndicator
                dataUpdatedAt={dataUpdatedAt}
                onRefresh={() => refetch()}
                isRefetching={isRefetching}
              />
            </div>
          )}

          {/* Meal sections */}
          <div className="space-y-4">
            {meals &&
              (["breakfast", "lunch", "dinner"] as const).map((mealName) => (
                <KitchenMealSection
                  key={mealName}
                  mealName={mealName}
                  meal={meals[mealName]}
                  onGuestClick={setSelectedGuest}
                />
              ))}
          </div>
        </>
      )}
    </div>
  )
}
